import { useEffect, useState } from "react";
import { API } from "../../api/API";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import { useSelector } from "react-redux";
import { RootState } from "../../redux-toolkit/store";
import { Card, Input } from "reactstrap";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import FeatherIconCom from "../../Utils/CommonComponents/CommonIcons/FeatherIconCom";
import DatePicker from "react-datepicker";
import TreeModel from "../Dashboards/Attendance/AttendanceTable/TreeModel";
import { getFilteredPositionIds } from "../../db/DexieServises";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Image } from "../../AbstractElements";
import { dynamicImage } from "../../Utils";
import CommonTableNew from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";

interface TeamAttendanceRow {
  EmployeeId: number;
  EmployeeCode: string;
  FirstName: string;
  LastName: string;
  PositionId: number;
  PositionName: string;
  AttendanceStatus: string;
  AttendanceDate: string;
  StartTime: string;
  EndTime: string;
  StartTown: string;
  EndTown: string;
  TravelledDistance: string;
  BeatNamesWithCount: string;
  ActualBeatNamesWithCount: string;
  OrderTotal: number;
}

const TeamAttendanceReport = () => {
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatTo12Hour = (timeStr: string): string => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const [tableData, setTableData] = useState<TeamAttendanceRow[]>([]);
  const [displayedData, setDisplayedData] = useState<TeamAttendanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const today = new Date();

  const [fromDate, setFromDate] = useState<Date>(today);
  const [toDate, setToDate] = useState<Date>(today);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  const orgUnitId = localStorage.getItem("orgUnitId");
  const serverUrl = localStorage.getItem("serverUrl");

  // Fetch position IDs when unitId changes
  useEffect(() => {
    const fetchPositionIds = async () => {
      if (unitId) {
        const ids = await getFilteredPositionIds("Position", unitId);
        setSelectedIds(ids);
      }
    };
    fetchPositionIds();
  }, [unitId]);

  // Fetch team attendance report
  useEffect(() => {
    if (!fromDate || !toDate) return;

    const fetchTeamAttendanceReport = async () => {
      try {
        setLoading(true);

        const params: Record<string, string> = {
          date_range: `${formatDate(fromDate)}_${formatDate(toDate)}`,
        };

        // Add org_unit_id only if viewing a different org unit
        if (String(unitId) !== String(orgUnitId)) {
          params.org_unit_id = String(unitId);
        }

        const url = `${serverUrl}${API.getTeamAttendance}`;
        const { status, data } = await getData(url, true, params);

        if (status === 200 && data?.data) {
          setTableData(data.data);
        } else {
          setTableData([]);
        }
      } catch (error) {
        console.error(error);
        setTableData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamAttendanceReport();
  }, [fromDate, toDate, serverUrl, unitId, orgUnitId, refreshTrigger]);

  // Filter data based on search text and selected positions
  useEffect(() => {
    const lower = searchText.toLowerCase();

    const filtered = tableData.filter((item) => {
      const fullName = `${item.FirstName ?? ""} ${item.LastName ?? ""}`.toLowerCase();

      const matchesSearch =
        fullName.includes(lower) ||
        item.EmployeeCode?.toLowerCase().includes(lower) ||
        item.PositionName?.toLowerCase().includes(lower) ||
        item.AttendanceStatus?.toLowerCase().includes(lower) ||
        item.StartTown?.toLowerCase().includes(lower) ||
        item.EndTown?.toLowerCase().includes(lower);

      const matchesSelection =
        selectedIds.length === 0 || selectedIds.includes(item.PositionId);

      return matchesSearch && matchesSelection;
    });

    setDisplayedData(filtered);
  }, [searchText, tableData, selectedIds]);

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = displayedData.map((row) => ({
      "Employee Code": row.EmployeeCode,
      Name: `${row.FirstName ?? ""} ${row.LastName ?? ""}`,
      Position: row.PositionName,
      "Attendance Date": formatDisplayDate(row.AttendanceDate),
      Status: row.AttendanceStatus,
      "Start Time": row.StartTime ? formatTo12Hour(row.StartTime) : "-",
      "End Time": row.EndTime ? formatTo12Hour(row.EndTime) : "-",
      "Start Town": row.StartTown || "-",
      "End Town": row.EndTown || "-",
      "Travelled KM": row.TravelledDistance || "-",
      "Planned Patch": row.BeatNamesWithCount || "-",
      "Actual Patch": row.ActualBeatNamesWithCount || "-",
      POB: row.OrderTotal ?? 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Team Attendance");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Team_Attendance_Report_${formatDate(fromDate)}_to_${formatDate(toDate)}.xlsx`
    );
  };

  // Table head data for CommonTableNew
  const tableHeadData = [
    { id: "1", head: "Emp Code", key: "EmployeeCode", sortKey: "EmployeeCode" },
    { id: "2", head: "Name", key: "fullName", sortKey: "FirstName" },
    { id: "3", head: "Position", key: "PositionName", sortKey: "PositionName" },
    { id: "4", head: "Date", key: "AttendanceDate", sortKey: "AttendanceDate" },
    { id: "5", head: "Status", key: "AttendanceStatus", sortKey: "AttendanceStatus" },
    { id: "6", head: "Start-End Time", key: "travelTime", sortKey: null },
    { id: "7", head: "Start-End Town", key: "travelTown", sortKey: null },
    { id: "8", head: "Travelled KM", key: "TravelledDistance", sortKey: "TravelledDistance" },
    { id: "9", head: "Planned Patch", key: "BeatNamesWithCount", sortKey: null },
    { id: "10", head: "Actual Patch", key: "ActualBeatNamesWithCount", sortKey: null },
    { id: "11", head: "POB", key: "OrderTotal", sortKey: "OrderTotal" },
  ];

  return (
    <div className="page-body mt-5 p-2">
      <Card>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-end pt-4 pb-2">
          <CardHeaderSpan
            headingClassName="pb-0"
            back={true}
            heading="Team Attendance Report"
            span="View team attendance data with filters"
          />            <TreeModel onSelectionChange={setSelectedIds} />

        </div>

        {/* Filters Row */}
        <div className="row px-3 mt-3 align-items-start justify-content-between">
          <div className="col-md-6 d-flex align-items-center gap-3 flex-wrap">
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by name, code, position..."
              className="form-control"
              style={{ maxWidth: "250px" }}
            />
          </div>

          <div className="col-md-6 d-flex align-items-center justify-content-end gap-3">
            <label className="form-label mb-0 align-items-start small text-muted">From</label>
            <DatePicker
              selected={fromDate}
              onChange={(date: Date | null) => date && setFromDate(date)}
              maxDate={new Date()}
              dateFormat="dd-MM-yyyy"
              customInput={
                <input
                  className="form-control form-control-sm"
                  style={{ width: "120px", height: "35px" }}
                />
              }
            />
            <label className="form-label mb-0 small text-muted">To</label>
            <DatePicker
              selected={toDate}
              onChange={(date: Date | null) => date && setToDate(date)}
              maxDate={new Date()}
              dateFormat="dd-MM-yyyy"
              customInput={
                <input
                  className="form-control form-control-sm"
                  style={{ width: "120px", height: "35px" }}
                />
              }
            />
            <button
              className="btn btn-sm align-items-center d-flex"
              style={{ backgroundColor: "orange" }}
              onClick={() => setRefreshTrigger((p) => !p)}
            >
              <FeatherIconCom
                iconName={"RotateCw"}
                size={14}
                className="text-light"
              />
              <span className="px-2 text-light" style={{ fontSize: 14 }}>
                Refresh
              </span>
            </button>
            <button
              className="btn btn-sm btn-primary align-items-center d-flex"
              onClick={handleExportToExcel}
              disabled={displayedData.length === 0}
            >
              <FeatherIconCom
                iconName={"Download"}
                size={14}
                className="text-light"
              />
              <span className="px-2" style={{ fontSize: 14 }}>
                Export
              </span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive custom-scrollbar mt-3 px-3 pb-3">
          {loading ? (
            <div className="text-center py-3">
              <Image
                className="b-r-10 img-80"
                src={dynamicImage("dashboard/loader.gif")}
                alt="Loading..."
              />
            </div>
          ) : (
            <CommonTableNew
              caption=""
              headData={tableHeadData}
              headClass="table"
              data={displayedData}
              renderRow={(row: TeamAttendanceRow, index: number) => (
                <tr key={index}>
                  <td>{row.EmployeeCode || "-"}</td>
                  <td style={{ textAlign: "left" }}>
                    {`${row.FirstName ?? ""} ${row.LastName ?? ""}`.trim() || "-"}
                  </td>
                  <td style={{ textAlign: "left" }}>{row.PositionName || "-"}</td>
                  <td>{formatDisplayDate(row.AttendanceDate) || "-"}</td>
                  <td>{row.AttendanceStatus || "-"}</td>
                  <td>
                    {row.StartTime || row.EndTime
                      ? `${formatTo12Hour(row.StartTime)} - ${formatTo12Hour(row.EndTime)}`
                      : "-"}
                  </td>
                  <td>
                    {row.StartTown || row.EndTown
                      ? `${row.StartTown ?? ""} → ${row.EndTown ?? ""}`
                      : "-"}
                  </td>
                  <td>{row.TravelledDistance || "-"}</td>
                  <td>{row.BeatNamesWithCount || "-"}</td>
                  <td>{row.ActualBeatNamesWithCount || "-"}</td>
                  <td>{row.OrderTotal ?? 0}</td>
                </tr>
              )}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default TeamAttendanceReport;
